import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-proto';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { OTLPExporterNodeConfigBase } from '@opentelemetry/otlp-exporter-base';
import { Resource } from '@opentelemetry/resources';
import {
    ConsoleSpanExporter,
    SimpleSpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import {
    BatchSpanProcessor,
    SpanExporter,
    SpanProcessor,
    WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { LogOtelSpanProcessorService } from './log-otel-span-processor.service';
import {
    ILogOtelConfig,
    ILogOtelConfigExporter,
    ILogOtelUtilsService,
    LogOtelExporters,
    LogOtelProcessors,
    LogOtelUser,
} from './models';

declare const window: Window;

export class LogOtelUtilsService implements ILogOtelUtilsService {
    private _provider: WebTracerProvider;

    constructor(
        private _processor: LogOtelSpanProcessorService,
        private _config: ILogOtelConfig,
    ) {}

    setUser(user: LogOtelUser): void {
        this._processor.setUser(user);
    }

    getProvider(): WebTracerProvider {
        return this._provider;
    }

    getTracer() {
        return this._provider.getTracer(this._config.serviceName);
    }

    initialize() {
        this.setUp();
    }

    setUp() {
        this._processor.setDefaultAttributes();
        this.setProvider();
        this.getProvider().addSpanProcessor(this._processor);
        this.addExporters(this._config.exporters);
        this.setContexts();
        this.registerIntrumentations();
        this.getProvider().register();
    }

    registerIntrumentations() {
        registerInstrumentations({
            instrumentations: [new DocumentLoadInstrumentation()],
        });
    }
    setContexts() {
        this.getProvider().register({
            contextManager: new ZoneContextManager(),
        });
    }
    addExporters(exporters: ILogOtelConfigExporter[] = []) {
        if (!this.getProvider()) this.setProvider();

        exporters.forEach((exporter: ILogOtelConfigExporter) => {
            this.addExporter(exporter);
        });
    }

    replaceExporters(exporters: ILogOtelConfigExporter[] = []) {
        if (!exporters.length) return;

        this.getProvider().shutdown();
        this.setProvider();

        exporters.forEach((exporter: ILogOtelConfigExporter) => {
            this.addExporter(exporter);
        });
    }

    addExporter(exporter: ILogOtelConfigExporter): SpanProcessor {
        const spanExporter: SpanExporter = this.getExporter(exporter);
        const spanProcessor: SpanProcessor = this.setSpanProcessor(
            exporter,
            spanExporter,
        );

        this.getProvider().addSpanProcessor(spanProcessor);

        return spanProcessor;
    }
    setSpanProcessor(
        exporter: ILogOtelConfigExporter,
        spanExporter: SpanExporter,
    ): SpanProcessor {
        return (
            {
                [LogOtelProcessors.Simple]: new SimpleSpanProcessor(
                    spanExporter,
                ),
                [LogOtelProcessors.Batch]: new BatchSpanProcessor(spanExporter),
            }[exporter.processor] ?? new BatchSpanProcessor(spanExporter)
        );
    }
    getExporter(exporter: ILogOtelConfigExporter): SpanExporter {
        return (
            {
                [LogOtelExporters.Console]: new ConsoleSpanExporter(),
                [LogOtelExporters.EndPoint]: new OTLPTraceExporter(
                    exporter.settings as OTLPExporterNodeConfigBase,
                ),
            }[exporter.type] ?? new ConsoleSpanExporter()
        );
    }

    setProvider() {
        const resource = Resource.default().merge(
            new Resource({
                [SemanticResourceAttributes.SERVICE_NAME]:
                    this._config.serviceName,
                [SemanticResourceAttributes.SERVICE_VERSION]:
                    this._config.serviceVersion,
            }),
        );

        this._provider = new WebTracerProvider({
            resource,
        });
    }
}
